import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  // Send the httpOnly refresh-token cookie with requests (needed for /auth/refresh).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const authStore = useAuthStore.getState();
    
    // If 401 and not already retried, try to refresh the token (via cookie).
    // Never attempt this for the auth endpoints themselves — otherwise a 401 on
    // /logout or /refresh would trigger another refresh→logout cycle, which is an
    // infinite loop (each axios call re-enters this interceptor).
    const url = originalRequest?.url || '';
    const isAuthEndpoint =
      url.includes('/api/auth/refresh') ||
      url.includes('/api/auth/logout') ||
      url.includes('/api/auth/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const newToken = await authStore.refreshTokenFn();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed — clear auth state locally only (no network call) so the
        // app falls back to the login screen without looping.
        authStore.clearAuth();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Reservation API
export const reservationApi = {
  // Get all reservations
  getAll: (params = {}) => api.get('/api/reservations', { params }),
  
  // Get my reservations
  getMyReservations: (params = {}) => api.get('/api/reservations/me', { params }),
  
  // Get available slots
  getAvailableSlots: (params) => api.get('/api/reservations/available-slots', { params }),
  
  // Get single reservation
  getById: (id) => api.get(`/api/reservations/${id}`),
  
  // Create reservation
  create: (data) => api.post('/api/reservations', data),
  
  // Update reservation
  update: (id, data) => api.put(`/api/reservations/${id}`, data),
  
  // Move reservation (admin only)
  move: (id, data) => api.patch(`/api/reservations/${id}/move`, data),
  
  // Delete reservation
  delete: (id) => api.delete(`/api/reservations/${id}`),
  
  // Cancel reservation
  cancel: (id) => api.delete(`/api/reservations/${id}`)
};

// Admin API
export const adminApi = {
  // Users
  getUsers: (params = {}) => api.get('/api/admin/users', { params }),
  getPendingUsers: () => api.get('/api/admin/users/pending'),
  approveUser: (userId) => api.post(`/api/admin/users/${userId}/approve`),
  makeAdmin: (userId) => api.post(`/api/admin/users/${userId}/make-admin`),
  removeAdmin: (userId) => api.post(`/api/admin/users/${userId}/remove-admin`),
  deactivateUser: (userId) => api.post(`/api/admin/users/${userId}/deactivate`),
  activateUser: (userId) => api.post(`/api/admin/users/${userId}/activate`),
  
  // Reservations
  getAllReservations: (params = {}) => api.get('/api/admin/reservations', { params }),
  
  // Statistics
  getStatistics: (params = {}) => api.get('/api/admin/statistics', { params }),
  
  // Audit logs
  getAuditLogs: (params = {}) => api.get('/api/admin/audit-logs', { params })
};

// User API
export const userApi = {
  getProfile: () => api.get('/api/users/profile'),
  updateProfile: (data) => api.put('/api/users/profile', data),
  getMeetingRoom: () => api.get('/api/users/meeting-room'),
  getMyReservations: (params = {}) => api.get('/api/users/my-reservations', { params }),
  getCancelledReservations: (params = {}) => api.get('/api/users/cancelled-reservations', { params })
};

// MCP API
export const mcpApi = {
  sendEmail: (data) => api.post('/api/mcp/send-email', data),
  sendReservationNotification: (data) => api.post('/api/mcp/send-reservation-notification', data),
  processQueue: () => api.post('/api/mcp/process-queue'),
  getQueueStatus: () => api.get('/api/mcp/queue-status'),
  testConnection: () => api.post('/api/mcp/test-connection'),
  getConfig: () => api.get('/api/mcp/config')
};

// Meeting Room API
export const meetingRoomApi = {
  getInfo: () => api.get('/api/users/meeting-room')
};

export default api;
